/**
 * Home (JSS#1155): one card per surface the user can reach, each with a few
 * real numbers, its primary action and, for an org with nothing there yet, a
 * "get started" action. Replaces the streaming-only Dashboard and its
 * ops-agent panel (platform health is corp-console's, #1156).
 *
 * Every card fetches on its own and fails on its own: one service being down
 * shows "Unavailable" in its card and nowhere else.
 *
 * Cards follow the acting org's surface entitlements (#1160): a surface the
 * org doesn't have gets no card, so its API (which the BFF would 403) is never
 * called. While the entitlements load, gated cards wait rather than fetch.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { channelsApi, eventsApi, getOrgOverride, processingApi, sitesApi, sourcesApi } from '@/api/client';
import { forwardsNoOrg } from '@/components/layout/OrgRequiredGate';
import { processingKeys } from '@/hooks/useProcessing';
import { useEntitlements } from '@/hooks/useEntitlements';
import { hasJbsConsole } from '@/lib/siteUrls';
import { visibleSurfaces, type Surface } from '@/surfaces';
import type { SiteStatus } from '@/types';

/** Surfaces Home has no card for: itself, and the account pages. */
const NO_CARD = new Set(['home', 'org']);

// ---- Card frame ----

interface Stat {
  label: string;
  /** Null when its API failed while the card's others answered. */
  value: number | null;
  tone?: 'error';
}

interface Action {
  to: string;
  label: string;
}

type CardBody =
  | { kind: 'loading' }
  | { kind: 'unavailable' }
  /** The surface cannot show anything in this view (e.g. org required). */
  | { kind: 'hint'; text: string }
  | { kind: 'empty'; text: string; action: Action }
  | { kind: 'stats'; stats: Stat[]; action: Action };

function SurfaceCard({ surface, body }: { surface: Surface; body: CardBody }) {
  const Icon = surface.icon;
  return (
    <section
      aria-label={surface.label}
      data-testid={`home-card-${surface.id}`}
      className="bg-jeket-black-surface border border-jeket-black-elevated rounded-xl p-5 flex flex-col gap-4"
    >
      <header className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-jeket-silver-bright">{surface.label}</h3>
        <Icon size={18} className="text-jeket-gold" aria-hidden />
      </header>

      <div className="flex-1">
        {body.kind === 'loading' && <p className="text-sm text-jeket-silver-muted">Loading…</p>}
        {body.kind === 'unavailable' && <p className="text-sm text-jeket-silver-muted">Unavailable</p>}
        {body.kind === 'hint' && <p className="text-sm text-jeket-silver-muted">{body.text}</p>}
        {body.kind === 'empty' && <p className="text-sm text-jeket-silver-muted">{body.text}</p>}
        {body.kind === 'stats' && (
          <dl className="grid grid-cols-2 gap-3">
            {body.stats.map((s) => (
              <div key={s.label}>
                <dt className="text-xs text-jeket-silver-muted">{s.label}</dt>
                <dd
                  className={
                    s.value === null
                      ? 'text-sm text-jeket-silver-muted mt-1'
                      : `text-2xl font-bold mt-0.5 ${s.tone === 'error' && s.value > 0 ? 'text-jeket-error' : 'text-jeket-silver-bright'}`
                  }
                >
                  {s.value === null ? 'Unavailable' : s.value}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </div>

      {(body.kind === 'empty' || body.kind === 'stats') && (
        <Link
          to={body.action.to}
          className={
            body.kind === 'empty'
              ? 'inline-flex items-center justify-center gap-1.5 self-start px-3 py-1.5 rounded-lg text-sm font-medium bg-jeket-gold text-jeket-black-deep hover:bg-jeket-gold-light transition-colors'
              : 'inline-flex items-center gap-1 self-start text-sm text-jeket-gold hover:text-jeket-gold-light transition-colors'
          }
        >
          {body.action.label}
          <ArrowRight size={14} aria-hidden />
        </Link>
      )}
    </section>
  );
}

/** The card's state from its queries; `null` stats are the failed ones. */
function bodyFrom(
  queries: UseQueryResult<unknown>[],
  build: () => { empty: boolean; onEmpty: { text: string; action: Action }; stats: Stat[]; action: Action },
): CardBody {
  if (queries.some((q) => q.isLoading)) return { kind: 'loading' };
  if (queries.every((q) => q.isError)) return { kind: 'unavailable' };
  const { empty, onEmpty, stats, action } = build();
  // Only call it empty when every query answered: a failed one may hold data.
  if (empty && queries.every((q) => q.isSuccess)) return { kind: 'empty', ...onEmpty };
  return { kind: 'stats', stats, action };
}

const valueOf = <T,>(q: UseQueryResult<T>, f: (d: T) => number): number | null =>
  q.isSuccess ? f(q.data) : null;

// ---- Per-surface cards ----

function useSites() {
  return useQuery({ queryKey: ['sites'], queryFn: sitesApi.list });
}

/** The SiteStatus values that are an error state: failing checks, or unreachable. */
const NEEDS_ATTENTION: ReadonlySet<SiteStatus> = new Set<SiteStatus>(['degraded', 'offline']);

function SitesCard({ surface }: { surface: Surface }) {
  const sites = useSites();
  const body = bodyFrom([sites], () => ({
    empty: (sites.data ?? []).length === 0,
    onEmpty: {
      text: 'No sites yet. A site is your on-prem cluster: add one to install Jeket on your hardware.',
      action: { to: '/sites', label: 'Add your first site' },
    },
    stats: [
      { label: 'Total', value: valueOf(sites, (d) => d.length) },
      { label: 'Need attention', value: valueOf(sites, (d) => d.filter((s) => NEEDS_ATTENTION.has(s.status)).length), tone: 'error' },
    ],
    action: { to: '/sites', label: 'View sites' },
  }));
  return <SurfaceCard surface={surface} body={body} />;
}

function ProductionCard({ surface }: { surface: Surface }) {
  const sites = useSites();
  const body = bodyFrom([sites], () => ({
    empty: !(sites.data ?? []).some(hasJbsConsole),
    onEmpty: {
      text: 'No site runs JBS yet. Install JBS on a site to run productions there.',
      action: { to: '/sites', label: 'Install JBS on a site' },
    },
    stats: [{ label: 'Sites with JBS', value: valueOf(sites, (d) => d.filter(hasJbsConsole).length) }],
    action: { to: '/production', label: 'Open productions' },
  }));
  return <SurfaceCard surface={surface} body={body} />;
}

function AudienceCard({ surface }: { surface: Surface }) {
  const events = useQuery({ queryKey: ['events', 'home'], queryFn: () => eventsApi.list() });
  // The Events page's Upcoming tab, so the number matches the list it links
  // to: event-manager's `scheduled_start > NOW()`, whatever the status.
  const upcoming = useQuery({ queryKey: ['events', 'home', 'upcoming'], queryFn: () => eventsApi.list({ upcoming: 'true' }) });
  const body = bodyFrom([events, upcoming], () => ({
    empty: (events.data ?? []).length === 0,
    onEmpty: {
      text: 'No events yet. Create one to give viewers and guests a page to join.',
      action: { to: '/audience/events', label: 'Create an event' },
    },
    stats: [
      { label: 'Live now', value: valueOf(events, (d) => d.filter((e) => e.status === 'live').length) },
      { label: 'Upcoming', value: valueOf(upcoming, (d) => d.length) },
    ],
    action: { to: '/audience/events', label: 'View events' },
  }));
  return <SurfaceCard surface={surface} body={body} />;
}

function StreamingCard({ surface }: { surface: Surface }) {
  const channels = useQuery({ queryKey: ['channels'], queryFn: channelsApi.list });
  const sources = useQuery({ queryKey: ['sources'], queryFn: sourcesApi.list });
  const body = bodyFrom([channels, sources], () => ({
    empty: (channels.data ?? []).length === 0 && (sources.data ?? []).length === 0,
    onEmpty: {
      text: 'No channels yet. A channel turns your sources into a linear stream with a schedule.',
      action: { to: '/streaming/channels', label: 'Create a channel' },
    },
    stats: [
      { label: 'Channels', value: valueOf(channels, (d) => d.length) },
      { label: 'Active sources', value: valueOf(sources, (d) => d.filter((s) => s.status === 'active').length) },
    ],
    action: { to: '/streaming/channels', label: 'View channels' },
  }));
  return <SurfaceCard surface={surface} body={body} />;
}

function MediaCard({ surface }: { surface: Surface }) {
  const { user, meStatus } = useAuth();
  // A session the BFF forwards no org for (a superuser in "All
  // Organizations", staff in no org, a member of several orgs with no pick)
  // has no org to read media from (orgRequired, #1149): ask for one instead
  // of calling the API. Same rule as OrgRequiredGate, so the card and the
  // Media pages never disagree.
  const noOrg = !!surface.orgRequired && forwardsNoOrg(user, meStatus, getOrgOverride());
  const recordings = useQuery({
    queryKey: processingKeys.recordings(),
    queryFn: () => processingApi.listRecordings(),
    enabled: !noOrg,
  });
  const clips = useQuery({
    queryKey: processingKeys.clips(),
    queryFn: () => processingApi.listClips(),
    enabled: !noOrg,
  });
  if (noOrg) {
    return (
      <SurfaceCard
        surface={surface}
        body={{ kind: 'hint', text: 'Pick an organization from the menu to see its recordings and clips.' }}
      />
    );
  }
  const body = bodyFrom([recordings, clips], () => ({
    empty: (recordings.data?.recordings ?? []).length === 0 && (clips.data?.clips ?? []).length === 0,
    onEmpty: {
      text: 'No recordings yet. Events and sources are recorded here as they go live.',
      action: { to: '/media/settings', label: 'Set up processing' },
    },
    stats: [
      { label: 'Recordings', value: valueOf(recordings, (d) => d.recordings.length) },
      { label: 'Clips to review', value: valueOf(clips, (d) => d.clips.filter((c) => !c.approved && !c.rejected).length) },
    ],
    action: { to: '/media/recordings', label: 'View recordings' },
  }));
  return <SurfaceCard surface={surface} body={body} />;
}

const CARDS: Record<string, React.ComponentType<{ surface: Surface }>> = {
  production: ProductionCard,
  audience: AudienceCard,
  streaming: StreamingCard,
  media: MediaCard,
  sites: SitesCard,
};

// ---- Page ----

export default function Home() {
  const { user } = useAuth();
  const { entitlements, isLoading } = useEntitlements();
  const waiting = isLoading && user?.role !== 'superuser';
  const surfaces = visibleSurfaces(user, entitlements?.surfaces).filter(
    (s) => !NO_CARD.has(s.id) && CARDS[s.id] && !(waiting && s.entitlement),
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-jeket-silver-bright">Home</h2>
        <p className="text-sm text-jeket-silver-muted mt-1">What your organization has in each part of Jeket.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {surfaces.map((s) => {
          const Card = CARDS[s.id];
          return <Card key={s.id} surface={s} />;
        })}
      </div>
    </div>
  );
}
